import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
/**
 * Modal overlay showing the full agent plan with status indicators.
 * Uses the glassmorphic design system: dark glass background, green accents, strong blur.
 */
export const PlanModal = ({ plan, isOpen, onClose }) => {
    const dialogRef = useRef(null);
    // Focus trap + Escape key
    useEffect(() => {
        if (!isOpen)
            return;
        dialogRef.current?.focus();
        const handleKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);
    // Prevent body scroll while open
    useEffect(() => {
        if (!isOpen)
            return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [isOpen]);
    if (!isOpen)
        return null;
    const completed = plan.entries.filter((e) => e.status === 'completed').length;
    const total = plan.entries.length;
    return (_jsxs("div", { style: { position: 'fixed', inset: 0, zIndex: 50 }, role: "dialog", "aria-modal": "true", "aria-label": "Agent plan progress", children: [_jsx("div", { style: {
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)',
                    transition: 'opacity 0.15s',
                }, onClick: onClose }), _jsx("div", { style: { display: 'flex', minHeight: '100%', alignItems: 'center', justifyContent: 'center', padding: 16 }, children: _jsxs("div", { ref: dialogRef, tabIndex: -1, style: {
                        position: 'relative',
                        maxWidth: 480,
                        width: '100%',
                        outline: 'none',
                        backgroundColor: 'rgba(8, 15, 12, 0.55)',
                        backdropFilter: 'blur(24px) saturate(1.35)',
                        WebkitBackdropFilter: 'blur(24px) saturate(1.35)',
                        border: '1px solid rgba(34, 197, 94, 0.12)',
                        borderRadius: 14,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    }, children: [_jsxs("div", { style: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                borderBottom: '1px solid rgba(34, 197, 94, 0.12)',
                            }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8 }, children: [_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "rgba(34, 197, 94, 0.7)", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M9 11l3 3L22 4" }), _jsx("path", { d: "M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" })] }), _jsx("h3", { style: { fontSize: '0.875rem', fontWeight: 600, color: '#e6f2ee', margin: 0 }, children: "Plan" }), _jsxs("span", { style: { fontSize: '0.75rem', color: '#9fb7ae' }, children: [completed, " of ", total, " complete"] })] }), _jsx("button", { type: "button", onClick: onClose, style: {
                                        padding: 4,
                                        borderRadius: 6,
                                        border: 'none',
                                        background: 'transparent',
                                        color: '#9fb7ae',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }, "aria-label": "Close plan", children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) })] }), _jsx("div", { style: { padding: '12px 16px', maxHeight: '60vh', overflowY: 'auto' }, children: _jsx("ul", { style: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }, children: plan.entries.map((entry, idx) => (_jsxs("li", { style: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.875rem' }, children: [_jsx("span", { style: {
                                                display: 'inline-block',
                                                height: 10,
                                                width: 10,
                                                borderRadius: '50%',
                                                marginTop: 5,
                                                flexShrink: 0,
                                                backgroundColor: entry.status === 'completed' ? '#22c55e' :
                                                    entry.status === 'in_progress' ? '#22c55e' : '#4a6a60',
                                                boxShadow: entry.status === 'in_progress' ? '0 0 8px rgba(34, 197, 94, 0.6)' : 'none',
                                                animation: entry.status === 'in_progress' ? 'glowPulse 2s ease-in-out infinite' : 'none',
                                            } }), _jsx("span", { style: {
                                                color: entry.status === 'completed' ? '#9fb7ae' : '#e6f2ee',
                                                textDecoration: entry.status === 'completed' ? 'line-through' : 'none',
                                                opacity: entry.status === 'completed' ? 0.7 : 1,
                                            }, children: entry.content })] }, idx))) }) }), _jsx("div", { style: { padding: '0 16px 12px' }, children: _jsx("div", { style: { height: 6, backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: 9999, overflow: 'hidden' }, children: _jsx("div", { style: {
                                        height: '100%',
                                        backgroundColor: '#22c55e',
                                        borderRadius: 9999,
                                        transition: 'width 0.3s ease',
                                        width: total > 0 ? `${(completed / total) * 100}%` : '0%',
                                        boxShadow: '0 0 8px rgba(34, 197, 94, 0.4)',
                                    } }) }) })] }) })] }));
};
//# sourceMappingURL=PlanModal.js.map